import React, { useCallback, useState, useEffect } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Typography, Image } from 'lib/components';
import { Checkbox } from 'lib/components/FormFields';
import ItemRow from './ItemRow';
import { useWindowResize } from 'hooks/useWindowResize';
import { AllCheckedStatus } from 'types';
import classes from './index.module.scss';
import { HeaderItem, Sort, SortField } from './types';

import { itemIDCompareFn, itemNameCompareFn } from './sort';

/**
 * Props interface for the HospitalItemsDataTable component
 */
interface Props {
	/** Array of hospital items to display in the table */
	items: any[];
	/** Whether all items are checked */
	allChecked: boolean;
	/** Status of all checked items (all, none, or some) */
	allCheckedStatus: AllCheckedStatus;
	/** Callback function when the "select all" checkbox is clicked */
	handleAllCheckAction: (e: React.ChangeEvent<HTMLInputElement>) => void;
	/** Callback function when an individual item checkbox is clicked */
	checkAction: (id: number, isChecked: boolean) => void;
}

/**
 * Configuration for table header columns
 * Defines sortable columns with their initial sort direction
 */
const HEADER_ITEMS: HeaderItem[] = [
	{ label: 'ID', field: 'id', sort: 'asc' },
	{ label: 'Item Name', field: 'name', sort: 'asc' },
];

/**
 * HospitalItemsDataTable component
 * Displays a table of hospital items with sorting and selection capabilities
 * Supports selecting individual items or all items at once
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const HospitalItemsDataTable = ({ items, allChecked, allCheckedStatus, handleAllCheckAction, checkAction }: Props) => {
	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();

	// Initialize sort fields from header configuration
	const [sortFields] = useState(
		HEADER_ITEMS.filter((item) => item.field && item.sort).reduce<Record<string, Sort>>((acc, cur) => {
			if (cur.field && cur.sort) {
				acc[cur.field] = cur!.sort;
			}
			return acc;
		}, {})
	);

	// Current sort field and direction
	const [currentSortField, setCurrentSortField] = useState<SortField>({
		sort: 'asc',
		field: 'id',
	});
	const [sortedItems, setSortedItems] = useState<any[]>(items);

	/**
	 * Sort items based on the current sort field and direction
	 * Uses different comparison functions based on the field type
	 *
	 * @param {any[]} items - Array of items to sort
	 * @returns {any[]} - Sorted array of items
	 */
	const sortItems = useCallback(
		(items: any[]) => {
			// Create a copy and sort it based on the current sort field and direction
			const sorted = [...items].sort((a, b) => {
				switch (currentSortField?.sort) {
					case 'asc':
						// Ascending sort
						if (currentSortField?.field === 'name') {
							return itemNameCompareFn(a, b, 'asc');
						} else if (currentSortField?.field === 'id') {
							return itemIDCompareFn(a, b, 'asc');
						}

						return 0;

					case 'desc':
						// Descending sort
						if (currentSortField?.field === 'name') {
							return itemNameCompareFn(a, b, 'desc');
						} else if (currentSortField?.field === 'id') {
							return itemIDCompareFn(a, b, 'desc');
						}

						return 0;

					default:
						return 0;
				}
			});
			return sorted;
		},
		[currentSortField]
	);

	/**
	 * Update sorted items when the items prop changes
	 * Ensures the table displays the latest data with proper sorting
	 */
	useEffect(() => {
		setSortedItems(sortItems(items));
	}, [items, sortItems]);

	/**
	 * Re-sort items when the sort function changes
	 * Ensures the table reflects the current sort settings
	 */
	useEffect(() => {
		const sorted = sortItems(sortedItems);
		setSortedItems(sorted);
	}, [sortItems, setSortedItems]);

	/**
	 * Handle click on a sortable column header
	 * Toggles sort direction if clicking the same column
	 * Changes sort field if clicking a different column
	 *
	 * @param {HeaderItem} headerItem - Header item that was clicked
	 */
	const handleHeaderItemClick = useCallback(
		(headerItem: HeaderItem) => {
			if (headerItem.field && sortFields[headerItem.field]) {
				if (headerItem.field === currentSortField?.field) {
					// Toggle sort direction if clicking the same column
					setCurrentSortField((prev) => {
						return {
							...prev,
							sort: prev?.sort === 'asc' ? 'desc' : 'asc',
						};
					});
				} else if (currentSortField?.field !== headerItem.field) {
					// Change sort field if clicking a different column
					setCurrentSortField({ field: headerItem.field, sort: 'desc' });
				}
			}
		},
		[currentSortField, sortFields, setCurrentSortField]
	);

	/**
	 * Render a header item for the table
	 * Handles both sortable and non-sortable columns
	 * Displays sort indicators for sortable columns
	 *
	 * @param {HeaderItem} headerItem - Header item to render
	 * @returns {JSX.Element} - Rendered header item
	 */
	const renderHeaderItem = (headerItem: HeaderItem) => {
		if (!headerItem.field || !headerItem.sort) {
			// Non-sortable column header
			return (
				<div key={Math.random()} className={classes['col']}>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					{/* <Image src={'icTableDown'} /> */}
				</div>
			);
		} else {
			// Sortable column header with click handler
			return (
				<div
					onClick={() => handleHeaderItemClick(headerItem)}
					key={Math.random()}
					className={classes['col']}
					style={{
						cursor: 'pointer',
					}}
				>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					{/* Sort direction indicator */}
					<Image
						src={'icTableDown'}
						style={{
							marginLeft: '0.5rem',
							// Rotate the arrow based on sort direction
							transform:
								currentSortField?.field === headerItem.field && currentSortField.sort === 'desc'
									? 'rotate(180deg)'
									: 'initial',
						}}
					/>
				</div>
			);
		}
	};

	return (
		<div className={classes['items-datatable']}>
			<div className={classes['table-wrapper']}>
				<div className={classes['table-container']}>
					{/* Table header with column titles and select all checkbox */}
					<div className={classes['toolbar']}>
						{/* Select all checkbox */}
						<Checkbox
							checked={allChecked}
							onChange={handleAllCheckAction}
							style={{
								margin: '0 2rem 0.1rem 2rem',
							}}
						/>
						{/* Map through header items and render each column header */}
						{HEADER_ITEMS.map((item) => renderHeaderItem(item))}
					</div>

					{/* Table body with scrollable content */}
					<div className={classes['container']}>
						<Scrollbars
							style={{
								flex: 1,
							}}
						>
							{/* Map through sorted items and render each row */}
							{sortedItems.map((item) => (
								<ItemRow
									key={item.id}
									item={item}
									checkAction={checkAction}
									allCheckedStatus={allCheckedStatus}
								/>
							))}
						</Scrollbars>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HospitalItemsDataTable;
